import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Alert from '../components/Alert';

const PatientInfo = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const normalizeNotes = (value) => {
        if (typeof value !== 'string') return '';
        const normalized = value.trim().toLowerCase();
        return ['false', 'null', 'undefined'].includes(normalized) ? '' : value;
    };
    const [formData, setFormData] = useState({
        patientId: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        phoneNumber: '',
        email: '',
        notes: '',
        prescribingDoctor: ''
    });
    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [measurementId, setMeasurementId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [allowPatientEdit, setAllowPatientEdit] = useState(false);
    const [allowNotesEdit, setAllowNotesEdit] = useState(false);
    const [isOrdered, setIsOrdered] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [hideMeasurementsNavigation, setHideMeasurementsNavigation] = useState(false);
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);

    useEffect(() => {
        const metaTags = [
            ['Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0'],
            ['Pragma', 'no-cache'],
            ['Expires', '0'],
        ];

        const managedMetaTags = metaTags.map(([httpEquiv, content]) => {
            let meta = document.head.querySelector(`meta[http-equiv="${httpEquiv}"]`);
            const isCreated = !meta;

            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('http-equiv', httpEquiv);
                document.head.appendChild(meta);
            }

            meta.setAttribute('content', content);
            return { meta, isCreated };
        });

        const syncOfflineState = () => {
            setIsOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
        };

        syncOfflineState();
        window.addEventListener('online', syncOfflineState);
        window.addEventListener('offline', syncOfflineState);
        window.addEventListener('pageshow', syncOfflineState);

        return () => {
            window.removeEventListener('online', syncOfflineState);
            window.removeEventListener('offline', syncOfflineState);
            window.removeEventListener('pageshow', syncOfflineState);

            managedMetaTags.forEach(({ meta, isCreated }) => {
                if (isCreated && meta.parentNode) {
                    meta.parentNode.removeChild(meta);
                }
            });
        };
    }, []);

    // Pre-fill patient data if coming from Patients page
    useEffect(() => {
        if (location.state) {
            const state = location.state;
            console.log('PatientInfo - location.state:', {
                completedSteps: state.completedSteps,
                ordered: state.ordered,
                selectedProduct: state.selectedProduct,
                isReadOnly: state.isReadOnly
            });
            // Check if we have a patientId from the Patients list
            // Edit mode is only true when NOT creating a new measurement
            const hasExistingPatient = state.patientId && (state.patientFirstName || state.patientLastName);
            const isCreatingNewMeasurement = state.isNewMeasurement === true;
            setIsEditMode(hasExistingPatient && !isCreatingNewMeasurement);

            // Set read-only mode if flag is present
            if (state.isReadOnly) {
                setIsReadOnly(true);
            }

            setAllowPatientEdit(state.allowPatientEdit === true);
            setAllowNotesEdit(state.allowNotesEdit === true);
            setHideMeasurementsNavigation(state.hideMeasurementsNavigation === true);

            // Store measurementId if provided (for editing specific measurement)
            if (state.measurementId) {
                setMeasurementId(state.measurementId);
            }

            // Set ordered status if provided
            if (state.ordered !== undefined) {
                setIsOrdered(state.ordered);
            }

            // Set completed steps if provided
            if (state.completedSteps) {
                setCompletedSteps(state.completedSteps);
                console.log('PatientInfo - Setting completedSteps:', state.completedSteps);
            }

            setFormData(prev => {
                let firstName = state.firstName || state.patientFirstName || prev.firstName;
                let lastName = state.lastName || state.patientLastName || prev.lastName;

                // Legacy support: If coming from old code with patientName field
                if (!firstName && !lastName && state.patientName) {
                    const parts = state.patientName.trim().split(' ');
                    if (parts.length === 1) {
                        lastName = parts[0];
                    } else {
                        lastName = parts[parts.length - 1];
                        firstName = parts.slice(0, -1).join(' ');
                    }
                }

                return {
                    ...prev,
                    patientId: state.patientId || prev.patientId,
                    firstName,
                    lastName,
                    dateOfBirth: state.dateOfBirth || prev.dateOfBirth,
                    gender: state.gender || prev.gender,
                    address: state.address || state.patientAddress || prev.address,
                    phoneNumber: state.phoneNumber || prev.phoneNumber,
                    email: state.email || prev.email,
                    notes: state.notes !== undefined ? normalizeNotes(state.notes) : prev.notes,
                    prescribingDoctor: state.prescribingDoctor || prev.prescribingDoctor,
                };
            });
        }
    }, [location.state]);

    // Parse Date of Birth from Social Security ID (patientId)
    useEffect(() => {
        // Only parse if patientId has at least 6 digits and dateOfBirth is empty
        if (formData.patientId && formData.patientId.length >= 6 && !formData.dateOfBirth && !isReadOnly) {
            // Extract first 6 digits
            const digits = formData.patientId.replace(/\D/g, '').substring(0, 6);

            if (digits.length === 6) {
                try {
                    const yy = parseInt(digits.substring(0, 2), 10);
                    const mm = parseInt(digits.substring(2, 4), 10);
                    const dd = parseInt(digits.substring(4, 6), 10);

                    // Determine century: if YY <= current year's last 2 digits, assume 2000s, else 1900s
                    const currentYear = new Date().getFullYear();
                    const currentYY = currentYear % 100;
                    const yyyy = yy <= currentYY ? 2000 + yy : 1900 + yy;

                    // Create date object to validate
                    const parsedDate = new Date(yyyy, mm - 1, dd);

                    // Check if date is valid (handles invalid dates like Feb 30, etc.)
                    const isValidDate =
                        parsedDate.getFullYear() === yyyy &&
                        parsedDate.getMonth() === mm - 1 &&
                        parsedDate.getDate() === dd;

                    if (isValidDate) {
                        // Format as YYYY-MM-DD for HTML date input
                        const formattedDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
                        setFormData(prev => ({
                            ...prev,
                            dateOfBirth: formattedDate
                        }));
                    }
                } catch (error) {
                    // Parsing failed, do nothing
                    console.debug('Failed to parse date from Social Security ID');
                }
            }
        }
    }, [formData.patientId, formData.dateOfBirth, isReadOnly]);

    const handleChange = (e) => {
        // Prevent changes in read-only mode, except when measurement-row editing is explicitly enabled.
        if (isReadOnly && !(allowPatientEdit || (allowNotesEdit && e.target.name === 'notes'))) {
            return;
        }

        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field if it exists
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.patientId.trim()) {
            newErrors.patientId = t('patientInfo.validation.socialSecurityIdRequired');
        }

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('patientInfo.validation.firstNameRequired');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t('patientInfo.validation.lastNameRequired');
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = t('patientInfo.validation.phoneNumberRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSavePatientInfo = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        try {
            // Fetch all measurements for this patient
            const response = await axios.get('/api/measurements');
            const allMeasurements = response.data;

            // Filter measurements for this specific patient
            const patientMeasurements = allMeasurements.filter(
                m => m.patientId === formData.patientId
            );

            if (patientMeasurements.length === 0) {
                setError('No measurements found for this patient.');
                setIsSaving(false);
                return;
            }

            // If we have a specific measurementId, update only that record
            // Otherwise, update all measurements for this patient
            const measurementsToUpdate = measurementId
                ? patientMeasurements.filter(m => m._id === measurementId)
                : patientMeasurements;

            if (measurementsToUpdate.length === 0) {
                setError('Measurement record not found.');
                setIsSaving(false);
                return;
            }

            // Update the measurement(s) with new patient info
            // Only send the fields we want to update to avoid validation errors
            const updatePromises = measurementsToUpdate.map(measurement =>
                axios.put(`/api/measurements/${measurement._id}`, {
                    patientFirstName: formData.firstName,
                    patientLastName: formData.lastName,
                    patientDateOfBirth: formData.dateOfBirth,
                    patientGender: formData.gender,
                    patientAddress: formData.address,
                    patientPhoneNumber: formData.phoneNumber,
                    patientEmail: formData.email,
                    notes: formData.notes,
                    prescribingDoctor: formData.prescribingDoctor,
                })
            );

            await Promise.all(updatePromises);

            setSuccess('Patient information updated successfully!');
            setTimeout(() => navigate('/patients'), 1500);
        } catch (error) {
            console.error('Error updating patient info:', error);
            setError('Failed to update patient information. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isEditMode) {
            // If editing existing patient, save the changes
            handleSavePatientInfo();
        } else {
            // If creating new patient or new measurement, continue to measurements
            if (validateForm()) {
                // Store patient info in sessionStorage to use in subsequent steps
                sessionStorage.setItem('patientInfo', JSON.stringify(formData));

                // If this is for a new measurement (not a brand new patient),
                // fetch the latest measurement to pre-fill the form
                if (location.state?.isNewMeasurement && formData.patientId) {
                    try {
                        const response = await axios.get('/api/measurements');
                        const allMeasurements = response.data;

                        // Find measurements for this patient
                        const patientMeasurements = allMeasurements.filter(
                            m => m.patientId === formData.patientId
                        );

                        if (patientMeasurements.length > 0) {
                            // Sort by updatedAt to get the most recent
                            patientMeasurements.sort((a, b) =>
                                new Date(b.updatedAt) - new Date(a.updatedAt)
                            );
                            const latestMeasurement = patientMeasurements[0];
                            const nextNotes = formData.notes;
                            const normalizedMeasurementType = String(
                                latestMeasurement.measurementType
                                || latestMeasurement.selectedProduct?.productType
                                || ''
                            )
                                .trim()
                                .toLowerCase()
                                .replace(/[\s_-]/g, '');
                            const isMadeToMeasureLatest = normalizedMeasurementType === 'madetomeasure';

                            // Navigate with the latest measurement data pre-filled
                            navigate('/leg-measuring', {
                                state: {
                                    patientId: formData.patientId,
                                    patientFirstName: formData.firstName,
                                    patientLastName: formData.lastName,
                                    patientDateOfBirth: formData.dateOfBirth,
                                    patientGender: formData.gender,
                                    patientAddress: formData.address,
                                    patientPhoneNumber: formData.phoneNumber,
                                    patientEmail: formData.email,
                                    notes: nextNotes,
                                    prescribingDoctor: formData.prescribingDoctor,
                                    measurements: {
                                        ...(latestMeasurement.measurements || {}),
                                        notes: nextNotes,
                                    },
                                    measurements2: latestMeasurement.measurements2 || {},
                                    compressionClass: latestMeasurement.compressionClass || '',
                                    lengthType: latestMeasurement.lengthType || '',
                                    knitting: latestMeasurement.knitting || '',
                                    ...(isMadeToMeasureLatest && {
                                        measurementType: 'madeToMeasure',
                                        selectedProduct: latestMeasurement.selectedProduct || null,
                                        selections: latestMeasurement.selections || {},
                                    }),
                                }
                            });
                            return;
                        }
                    } catch (error) {
                        console.error('Error fetching latest measurements:', error);
                        // Continue anyway without pre-filled measurements
                    }
                }

                // Navigate to leg measurements (for brand new patients)
                navigate('/leg-measuring');
            }
        }
    };

    const handleCancel = () => {
        navigate('/patients');
    };

    const handleNavigateToMeasurements = () => {
        const nextNotes = formData.notes;

        // Navigate to leg measuring page with current patient info and state
        navigate('/leg-measuring', {
            state: {
                measurementId: measurementId,
                patientId: formData.patientId,
                patientFirstName: formData.firstName,
                patientLastName: formData.lastName,
                patientDateOfBirth: formData.dateOfBirth,
                patientGender: formData.gender,
                patientAddress: formData.address,
                patientPhoneNumber: formData.phoneNumber,
                patientEmail: formData.email,
                notes: nextNotes,
                prescribingDoctor: formData.prescribingDoctor,
                isReadOnly: isReadOnly,
                ordered: isOrdered,
                ...(location.state?.measurements && {
                    measurements: {
                        ...location.state.measurements,
                        notes: nextNotes,
                    },
                }),
                ...(location.state?.measurements2 && { measurements2: location.state.measurements2 }),
                ...(location.state?.compressionClass && { compressionClass: location.state.compressionClass }),
                ...(location.state?.lengthType && { lengthType: location.state.lengthType }),
                ...(location.state?.knitting && { knitting: location.state.knitting }),
                ...(location.state?.measurementType && { measurementType: location.state.measurementType }),
                ...(location.state?.selectedProduct && { selectedProduct: location.state.selectedProduct }),
                ...(location.state?.selections && { selections: location.state.selections }),
            }
        });
    };

    if (isOffline) {
        return (
            <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-xl rounded-2xl border border-yellow-200 bg-white p-8 shadow-lg">
                    <div className="mb-4 flex items-center gap-3 text-yellow-700">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h1 className="text-xl font-semibold">{t('patientInfo.offlineUnavailableTitle', { defaultValue: 'Patient information is unavailable offline' })}</h1>
                    </div>
                    <p className="text-sm text-gray-700">
                        {t('patientInfo.offlineUnavailableText', { defaultValue: 'This page requires a live connection and will not reuse cached data when the browser is offline.' })}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/patients')}
                            className="btn-primary"
                        >
                            {t('common.back')}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="btn-secondary"
                        >
                            {t('common.retry') || 'Retry'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-transition">
            <div className="container mx-auto px-4 py-8 max-w-3xl">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">{t('patientInfo.title')}</h1>
                    <p className="text-gray-600">
                        {t('patientInfo.subtitle')}
                    </p>
                </div>

                {/* Progress Indicator */}
                <div className="mb-8">
                    {console.log('PatientInfo - Rendering timeline with completedSteps:', completedSteps, 'isOrdered:', isOrdered)}
                    <div className="flex items-center justify-between text-sm">
                        {/* Step 1: Patient Info - Current Step (Blue) */}
                        <div className="flex items-center text-blue-600 font-semibold">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2">1</div>
                            <span>{t('patientInfo.progress.step1')}</span>
                        </div>

                        {/* Connector between Step 1 and Step 2 */}
                        <div className={`flex-1 h-1 ${completedSteps.includes('measurements') ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>

                        {/* Step 2: Measurements */}
                        <div className={`flex items-center ${completedSteps.includes('measurements') ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 ${completedSteps.includes('measurements') ? 'bg-green-600' : 'bg-gray-200'} ${completedSteps.includes('measurements') ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                {completedSteps.includes('measurements') ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    '2'
                                )}
                            </div>
                            <span>{t('patientInfo.progress.step2')}</span>
                        </div>

                        {/* Connector between Step 2 and Step 3 */}
                        <div className={`flex-1 h-1 ${completedSteps.includes('productSelection') ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>

                        {/* Step 3: Product Selection */}
                        <div className={`flex items-center ${completedSteps.includes('productSelection') ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 ${completedSteps.includes('productSelection') ? 'bg-green-600' : 'bg-gray-200'} ${completedSteps.includes('productSelection') ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                {completedSteps.includes('productSelection') ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    '3'
                                )}
                            </div>
                            <span>{t('patientInfo.progress.step3')}</span>
                        </div>

                        {/* Connector between Step 3 and Step 4 */}
                        <div className={`flex-1 h-1 ${completedSteps.includes('order') || isOrdered ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>

                        {/* Step 4: Order */}
                        <div className={`flex items-center ${completedSteps.includes('order') || isOrdered ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 ${completedSteps.includes('order') || isOrdered ? 'bg-green-600' : 'bg-gray-200'} ${completedSteps.includes('order') || isOrdered ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                {completedSteps.includes('order') || isOrdered ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    '4'
                                )}
                            </div>
                            <span>{t('patientInfo.progress.step4')}</span>
                        </div>
                    </div>
                </div>

                {/* Read-only mode indicator */}
                {isReadOnly && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-yellow-800 font-semibold">
                                {t('patientInfo.readOnlyWarning')}
                            </p>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="card">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Social Security ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.socialSecurityId')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="patientId"
                                value={formData.patientId}
                                onChange={handleChange}
                                disabled={isReadOnly}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.patientId ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.socialSecurityId')}
                            />
                            {errors.patientId && (
                                <p className="mt-1 text-sm text-red-500">{errors.patientId}</p>
                            )}
                        </div>

                        {/* Date of Birth */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.dateOfBirth')}
                            </label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.dateOfBirth && (
                                <p className="mt-1 text-sm text-red-500">{errors.dateOfBirth}</p>
                            )}
                        </div>

                        {/* Last Name - now first */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.lastName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.lastName')}
                            />
                            {errors.lastName && (
                                <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                            )}
                        </div>

                        {/* First Name - now second */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.firstName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.firstName')}
                            />
                            {errors.firstName && (
                                <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                            )}
                        </div>

                        {/* Gender - now optional */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.gender')}
                            </label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.gender ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            >
                                <option value="">{t('patientInfo.genderOptions.select')}</option>
                                <option value="male">{t('patientInfo.genderOptions.male')}</option>
                                <option value="female">{t('patientInfo.genderOptions.female')}</option>
                                <option value="other">{t('patientInfo.genderOptions.other')}</option>
                            </select>
                            {errors.gender && (
                                <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
                            )}
                        </div>

                        {/* Phone Number - now mandatory */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.phoneNumber')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                                    } ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.phoneNumber')}
                            />
                            {errors.phoneNumber && (
                                <p className="mt-1 text-sm text-red-500">{errors.phoneNumber}</p>
                            )}
                        </div>

                        {/* Address */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.address')}
                            </label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.address')}
                            />
                        </div>

                        {/* Email */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.email')}
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.email')}
                            />
                        </div>

                        {/* Notes */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.notes')}
                            </label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows="4"
                                disabled={isReadOnly && !(allowNotesEdit || allowPatientEdit)}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isReadOnly && !(allowNotesEdit || allowPatientEdit) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.notes')}
                            />
                        </div>

                        {/* Prescribing Doctor */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patientInfo.fields.prescribingDoctor')}
                            </label>
                            <input
                                type="text"
                                name="prescribingDoctor"
                                value={formData.prescribingDoctor}
                                onChange={handleChange}
                                disabled={isReadOnly && !allowPatientEdit}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isReadOnly && !allowPatientEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('patientInfo.placeholders.prescribingDoctor')}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between mt-8 pt-6 border-t">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {isReadOnly ? t('patientInfo.buttons.backToPatients') : t('patientInfo.buttons.cancel')}
                            </button>
                        </div>
                        {isEditMode && (!isReadOnly || allowNotesEdit || allowPatientEdit) ? (
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        {t('patientInfo.buttons.saving')}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('patientInfo.buttons.savePatientInfo')}
                                    </>
                                )}
                            </button>
                        ) : !isReadOnly ? (
                            <button
                                type="submit"
                                disabled={isReadOnly}
                                className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {t('patientInfo.buttons.continueToMeasurements')}
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : null}

                        {/* Next button for navigation when viewing existing measurement */}
                        {!hideMeasurementsNavigation && (isReadOnly || (measurementId && location.state?.measurements)) && (
                            <button
                                type="button"
                                onClick={handleNavigateToMeasurements}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                            >
                                {t('patientInfo.buttons.nextMeasurements')}
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}
                    </div>
                </form>

                {/* Alert Messages */}
                <Alert type="error" message={error} onDismiss={() => setError('')} />
                <Alert type="success" message={success} onDismiss={() => setSuccess('')} />
            </div>
        </div>
    );
};

export default PatientInfo;
